<template>
  <Layout
    class="onboarding-summary16167"
    :title="page.title"
    :scrollDefaultSlot="true"
  >
    <Summary16167Section
      v-if="whatPreventsYouChoices"
      title="What stops me from reading more"
      class="onboarding-summary16167__section onboarding-summary16167__section--what-prevents-you"
    >
      <Summary16167Choices
        pageID="what-prevents-you"
        :userChoices="whatPreventsYouChoices"
        :view="view"
      />
    </Summary16167Section>

    <Divider
      v-if="whatPreventsYouChoices"
      class="onboarding-summary16167__divider"
    />

    <Summary16167Section
      v-if="genreChoices"
      title="The main areas I want to improve in"
      class="onboarding-summary16167__section onboarding-summary16167__section--genres"
    >
      <Summary16167Choices
        pageID="genre-selection"
        :userChoices="genreChoices"
        :view="view"
      />
    </Summary16167Section>

    <Divider v-if="genreChoices" class="onboarding-summary16167__divider" />

    <Summary16167Section
      v-if="bookChoices"
      title="Your top books"
      class="onboarding-summary16167__section onboarding-summary16167__section--books"
    >
      <li
        v-for="(book, i) of bookChoices.slice(0, 5)"
        :key="book.id"
        class="onboarding-summary16167__book"
      >
        <img
          :src="cacheBust(book.cover)"
          alt=""
          width="158"
          height="211"
          class="onboarding-summary16167__cover"
        />
        <span
          v-if="i === 4 && bookChoices.length > 5"
          class="onboarding-summary16167__more-books"
        >
          +{{ bookChoices.length - 5 }}
        </span>
      </li>
    </Summary16167Section>
  </Layout>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import {
  BookSelection16167Choice,
  Summary16167Page,
} from '@/models/onboarding.types'
import { cacheBust } from '@/models/doc'

import Divider from '@/components/ui/Divider.vue'
import Layout from '../Layout.vue'
import Summary16167Section from '../Summary16167Section.vue'
import Summary16167Choices from '../Summary16167Choices.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as Summary16167Page
})

const whatPreventsYouChoices = computed(() => {
  return props.view.getUserChoice<string[]>('what-prevents-you')
})

const genreChoices = computed(() => {
  return props.view.getUserChoice<string[]>('genre-selection')
})

const bookChoices = computed(() => {
  return props.view.getUserChoice<BookSelection16167Choice>('book-selection')
})
</script>

<style lang="scss" scoped>
.onboarding-summary16167 {
  overflow-x: hidden;

  &:deep(.onboarding-layout__default-slot) {
    display: grid;
    justify-content: center;
    gap: 20px;
    width: calc(100% + var(--onboarding-padding) * 2);
    max-height: fit-content;
    padding-inline: var(--onboarding-padding);
    padding-top: var(--onboarding-padding);
  }

  &__divider {
    @media (max-width: 400px) {
      width: calc(100% + var(--onboarding-padding) * 2);
      max-width: none;
      margin-left: calc(var(--onboarding-padding) * -1);
    }
  }

  &__section--books {
    &:deep(.summary-section16167__choices) {
      gap: 0;
      align-items: center;
      justify-content: center;
      padding-top: 8px;
    }
  }

  &__book {
    position: relative;
    flex-shrink: 0;
    margin-top: -8px;
    margin-left: -8px;
    border-radius: 8px;
    border: 2px solid $c-white;
    filter: drop-shadow(-5px 4px 13px rgba(0, 0, 0, 0.15))
      drop-shadow(0 2px 8px rgba(0, 0, 0, 0.11));

    &:first-child {
      margin-left: 0;
    }
  }

  &__cover {
    width: auto;
    height: 80px;
    max-width: none;
  }

  &__more-books {
    position: absolute;
    right: -14px;
    top: calc(50% - 14px);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    font-size: 14px;
    font-weight: 500;
    background: $c-white;
    border-radius: 28px;
    filter: drop-shadow(-5px 4px 13px rgba(0, 0, 0, 0.15))
      drop-shadow(0 2px 8px rgba(0, 0, 0, 0.11));
  }
}
</style>
